import React, { useMemo } from "react";
import { Tooltip } from "@mui/material";
import {
  CountryWatchProviders,
  MovieDetailItem,
  MovieWatchProvidersResponse,
  WatchProvider,
} from "../../../../utils/types";
import {
  getProviderSearchUrl,
  getUserDeviceRegion,
} from "../../../../utils/watchProviders";
import { logMovieCTA } from "../../../../service/analytics";
import "./index.css";

interface MovieWatchProvidersProps {
  watchProviders: MovieWatchProvidersResponse | null;
  movieTitle: string;
  movieDetail: MovieDetailItem | null;
}

export default function MovieWatchProviders({
  watchProviders,
  movieTitle,
  movieDetail,
}: MovieWatchProvidersProps) {
  const userRegion = useMemo(() => getUserDeviceRegion(), []);

  const countryData: CountryWatchProviders | undefined = useMemo(() => {
    if (!watchProviders || !watchProviders.results || !userRegion) return undefined;
    // Strictly fetch providers for the user's detected region
    return watchProviders.results[userRegion];
  }, [watchProviders, userRegion]);

  // Consolidate streaming providers (flatrate, free, ads) without duplicates
  const streamProviders = useMemo(() => {
    if (!countryData) return [];
    const combined: WatchProvider[] = [
      ...(countryData.flatrate || []),
      ...(countryData.free || []),
      ...(countryData.ads || []),
    ];

    const seen = new Set<number>();
    return combined.filter((p) => {
      if (seen.has(p.provider_id)) return false;
      seen.add(p.provider_id);
      return true;
    });
  }, [countryData]);

  // Consolidate purchase / rental options (rent, buy) without duplicates
  const rentOrBuyProviders = useMemo(() => {
    if (!countryData) return [];
    const streamIds = new Set(streamProviders.map((p) => p.provider_id));
    const combined: WatchProvider[] = [
      ...(countryData.rent || []),
      ...(countryData.buy || []),
    ];

    const seen = new Set<number>();
    return combined.filter((p) => {
      // Avoid repeating providers already listed in stream if desired, or keep unique within rent/buy
      if (seen.has(p.provider_id) || streamIds.has(p.provider_id)) return false;
      seen.add(p.provider_id);
      return true;
    });
  }, [countryData, streamProviders]);

  if (!countryData || (streamProviders.length === 0 && rentOrBuyProviders.length === 0)) {
    return null;
  }

  const handleProviderClick = (provider: WatchProvider) => {
    if (movieDetail) {
      logMovieCTA("cta_click_watch_provider", movieDetail, {
        provider_name: provider.provider_name,
        provider_id: provider.provider_id,
        user_region: userRegion,
      });
    }
  };

  const renderProviderItem = (provider: WatchProvider) => {
    const url = getProviderSearchUrl(
      provider.provider_name,
      movieTitle,
      countryData.link,
    );

    return (
      <Tooltip
        key={provider.provider_id}
        title={`Watch "${movieTitle}" on ${provider.provider_name}`}
        arrow
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="watch-provider-item"
          onClick={() => handleProviderClick(provider)}
        >
          <img
            src={`${process.env.REACT_APP_RAPID_IMAGE_BASE_URL}/w92${provider.logo_path}`}
            alt={provider.provider_name}
            className="watch-provider-logo"
            loading="lazy"
          />
          <span className="watch-provider-name" title={provider.provider_name}>
            {provider.provider_name}
          </span>
        </a>
      </Tooltip>
    );
  };

  return (
    <div className="watch-providers-container">
      <h3 className="section-title">Where to Watch</h3>

      {streamProviders.length > 0 && (
        <div className="provider-category-block">
          <span className="provider-category-label">Stream</span>
          <div className="provider-grid">
            {streamProviders.map(renderProviderItem)}
          </div>
        </div>
      )}

      {rentOrBuyProviders.length > 0 && (
        <div className="provider-category-block">
          <span className="provider-category-label">Rent / Buy</span>
          <div className="provider-grid">
            {rentOrBuyProviders.map(renderProviderItem)}
          </div>
        </div>
      )}
    </div>
  );
}
